import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProductContext } from '../../providers/ProductContext';
import ProductCard from './ProductCard';

const LatestProducts = () => {
  const { products, loading, error, refetchProducts } = useContext(ProductContext);
  const latestProducts = useMemo(() => products.filter(product => product.isActive !== false).slice(0, 12), [products]);
  const trackRef = useRef(null);
  const [navigation, setNavigation] = useState({ previous: false, next: false });

  const updateNavigation = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const previous = track.scrollLeft > 1;
    const next = track.scrollLeft < track.scrollWidth - track.clientWidth - 1;
    setNavigation(current => current.previous === previous && current.next === next ? current : { previous, next });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    updateNavigation();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateNavigation) : null;
    observer?.observe(track);
    window.addEventListener('resize', updateNavigation);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateNavigation);
    };
  }, [latestProducts, updateNavigation]);

  const getScrollBehavior = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  const scrollProducts = direction => {
    const track = trackRef.current;
    if (!track) return;

    const width = track.firstElementChild?.getBoundingClientRect().width || track.clientWidth;
    const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
    const visibleProducts = Math.max(1, Math.floor((track.clientWidth + gap) / (width + gap)));
    track.scrollBy({ left: direction * visibleProducts * (width + gap), behavior: getScrollBehavior() });
  };

  const handleKeyDown = event => {
    if (event.target !== event.currentTarget) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      scrollProducts(event.key === 'ArrowLeft' ? -1 : 1);
    } else {
      const track = trackRef.current;
      track.scrollTo({ left: event.key === 'Home' ? 0 : track.scrollWidth, behavior: getScrollBehavior() });
    }
  };

  if (!loading && !error && latestProducts.length === 0) return null;

  const hasProducts = latestProducts.length > 0;
  const canScroll = navigation.previous || navigation.next;

  return (
    <section className="latest-products" aria-labelledby="latest-products-title" aria-busy={loading && !hasProducts}>
      <div className="container">
        <div className="latest-products-heading">
          <div>
            <span className="latest-products-kicker">Novedades en REM</span>
            <h2 id="latest-products-title">Recién llegados</h2>
            <p>Lo último que sumamos. Encontrá tu próximo favorito.</p>
          </div>
          <div className="latest-products-actions">
            <Link to="/tienda" className="latest-products-store-link">
              Ver toda la tienda <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </Link>
            {hasProducts && canScroll && (
              <div className="latest-products-controls" role="group" aria-label="Controles de novedades">
                <button
                  type="button"
                  aria-label="Productos anteriores"
                  aria-controls="latest-products-track"
                  disabled={!navigation.previous}
                  onClick={() => scrollProducts(-1)}
                >
                  <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label="Productos siguientes"
                  aria-controls="latest-products-track"
                  disabled={!navigation.next}
                  onClick={() => scrollProducts(1)}
                >
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </div>
        {hasProducts ? (
          <>
            <ul
              id="latest-products-track"
              className="latest-products-track"
              ref={trackRef}
              onScroll={updateNavigation}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              aria-label="Productos recién llegados"
            >
              {latestProducts.map(product => (
                <li className="latest-products-item" key={product._id}>
                  <ProductCard product={product} />
                </li>
              ))}
            </ul>
            {canScroll && <p className="latest-products-hint">Deslizá para descubrir más productos</p>}
          </>
        ) : loading ? (
          <>
            <span className="visually-hidden" role="status">Cargando novedades…</span>
            <div className="latest-products-track" aria-hidden="true">
              {Array.from({ length: 4 }, (_, index) => (
                <div className="latest-products-skeleton" key={index}>
                  <div className="latest-products-skeleton-image" />
                  <div className="latest-products-skeleton-info"><span /><span /></div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="latest-products-error" role="status">
            <p>No pudimos cargar las novedades. Podés intentar de nuevo.</p>
            <button type="button" className="latest-products-retry" onClick={() => refetchProducts()}>
              Reintentar
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default LatestProducts;
